// src/services/_base/BaseTenantCrudService.ts
import type { IFirestoreService } from '@/platform/firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export abstract class BaseTenantCrudService<T extends { id?: string }> {
  constructor(
    protected readonly collection: string,
    protected readonly firestore: IFirestoreService,
    protected readonly session: Session,
  ) {
    if (!session.tenantId) {
      throw new Error('tenantId is required for BaseTenantCrudService')
    }
  }

  protected getCollectionPath(): string {
    return `tenants/${this.session.tenantId}/${this.collection}`
  }

  async get(id: string): Promise<T | null> {
    return await this.firestore.getDoc<T>(this.getCollectionPath(), id)
  }

  async create(data: Partial<T>): Promise<string> {
    const payload = this.beforeCreate(data)
    const id = await this.firestore.add<T>(this.getCollectionPath(), payload as T)
    await this.afterCreate(id, payload)
    return id
  }

  async update(id: string, data: Partial<T>): Promise<void> {
    const payload = this.beforeUpdate(id, data)
    await this.firestore.updateDoc<T>(this.getCollectionPath(), id, payload)
    await this.afterUpdate(id, payload)
  }

  async delete(id: string): Promise<void> {
    await this.beforeDelete(id)
    await this.firestore.deleteDoc(this.getCollectionPath(), id)
    await this.afterDelete(id)
  }

  // ---- lifecycle hooks (override as needed) ----
  protected beforeCreate(data: Partial<T>): Partial<T> {
    const baseData = {
      ...data,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    // Inject session data if available (yardId and userId only, tenantId is handled by path)
    if (this.session.yardId) {
      ;(baseData as any).yardId = this.session.yardId
    }
    if (this.session.userId) {
      ;(baseData as any).userId = this.session.userId
    }

    return baseData
  }

  protected afterCreate(_id: string, _data: Partial<T>): void | Promise<void> {}
  protected beforeUpdate(_id: string, data: Partial<T>): Partial<T> {
    return { ...data, updatedAt: new Date() }
  }
  protected afterUpdate(_id: string, _data: Partial<T>): void | Promise<void> {}
  protected beforeDelete(_id: string): void | Promise<void> {}
  protected afterDelete(_id: string): void | Promise<void> {}
}
